import axios, { AxiosError } from 'axios';
import { useCallback } from 'react';

import { isRefusedError } from '@/api/api';
import { ANONYMOUS_FEEDBACK_MINIMUM_RESPONSES } from '@/constants/review/FeedbackPrivacy';
import i18n from '@/plugins/i18n';
import { captureError } from '@/plugins/Sentry';
import { showErrorToast } from '@/plugins/Toast';
import { translateRuntimeKey } from '@/utils/runtimeTranslation';

export interface ErrorProps {
    /*
     * The error object that was thrown
     */
    error?: unknown;
    /*
     * Optional data for displaying the error
     */
    data?: { title?: string; message: string };
}

export interface ResponseError extends Error {
    /* HTTP response associated with the error */
    response?: {
        status?: number;
    };
}

/** Values that the server's error sentences name but its `{ error }` body does not carry. */
const SERVER_ERROR_VALUES = {
    anonymousFeedbackMinimumResponses: ANONYMOUS_FEEDBACK_MINIMUM_RESPONSES,
};

/*
 * Checks if the given error is a ResponseError with a specific HTTP status code
 */
export const isResponseError = (error: unknown): error is ResponseError => {
    return (
        error instanceof Error &&
        typeof (error as ResponseError).response === 'object' &&
        (error as ResponseError).response !== null
    );
};

/*
 * Custom hook for handling errors in the application
 */
export const useError = () => {
    const handleError = useCallback(({ error, data }: ErrorProps) => {
        // Skip error handling for axios cancellations (including demo restrictions)
        if (axios.isCancel(error)) {
            return;
        }

        // Skip error handling for demo restriction cancellations (fallback check)
        if (error && typeof error === 'object' && 'isDemoRestriction' in error) {
            return;
        }

        const defaultError = {
            title: i18n.t('errors.genericTitle'),
            message: i18n.t('errors.genericMessage'),
        };

        let errorData = data ?? defaultError;

        // Translate the error key the server refused with: `error` in the older `{ error }` body, `detail` in a
        // problem document. Every problem document carries a detail, server faults included, so a sentence the
        // dictionary does not know leaves the caller's title and message untouched: "try again later" would
        // invite the user to repeat a request the server has just refused.
        if (error instanceof AxiosError && error.response?.data) {
            const responseData = error.response.data as { error?: string; detail?: string };
            const translated = translateRuntimeKey(
                `errors.${responseData.error ?? responseData.detail}`,
                SERVER_ERROR_VALUES,
            );
            if (translated !== null) {
                errorData = { title: i18n.t('errors.genericTitle'), message: translated };
            }
        }

        if (error instanceof AxiosError) {
            // Do not send error to Sentry if 401
            if (error.response?.status === 401) {
                return;
            }

            // A refusal is not a fault: it is neither worth a Sentry event nor a
            // second toast, because the response interceptor has already told the
            // user why their change was refused.
            if (isRefusedError(error)) {
                return;
            }

            const lastPathSegment =
                typeof window !== 'undefined'
                    ? window.location.pathname.split('/').filter(Boolean).pop() || 'root'
                    : 'root';

            captureError({ error, context: { tags: { page: lastPathSegment } } });
        }

        showErrorToast(errorData);
    }, []);

    return {
        handleError,
    };
};
